/**
 * Date utility functions for handling date formatting and parsing
 * to avoid timezone-related issues
 */

/**
 * Format a Date object to MM/DD/YYYY display format
 * @param {Date} date - The Date object to format
 * @returns {string} - Formatted date string (MM/DD/YYYY)
 */
export function formatDisplayDate(date) {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Format a Date object to YYYY-MM-DD input format
 * @param {Date} date - The Date object to format
 * @returns {string} - Formatted date string (YYYY-MM-DD)
 */
export function formatDateForInput(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD date string to a Date object without timezone issues
 * This manually parses the string to avoid timezone-related date shifts
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} - Parsed Date object
 */
export function parseDateString(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a date range display string from YYYY-MM-DD strings
 * Properly handles timezone issues when parsing the date strings
 * @param {string} startDateString - Start date in YYYY-MM-DD format
 * @param {string} endDateString - End date in YYYY-MM-DD format
 * @returns {string} - Formatted date range (MM/DD/YYYY - MM/DD/YYYY)
 */
export function formatDateRangeDisplay(startDateString, endDateString) {
  if (!startDateString || !endDateString) {
    return 'Select date range';
  }

  const startDate = parseDateString(startDateString);
  const endDate = parseDateString(endDateString);

  return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
}

/**
 * Apply quick date range selection (last 30 days, last 90 days, etc.)
 * @param {string} value - The quick range value ('last30days', 'last90days', etc.)
 * @returns {object} - Object with startDate and endDate in YYYY-MM-DD format
 */
export function applyQuickDateRange(value) {
  const endDate = new Date();
  let startDate = new Date();

  switch (value) {
    case 'last30days':
      startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case 'last90days':
      startDate = new Date(endDate.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case 'last180days':
      startDate = new Date(endDate.getTime() - 180 * 24 * 60 * 60 * 1000);
      break;
    case 'last12months':
      startDate = new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    case 'ytd':
      startDate = new Date(endDate.getFullYear(), 0, 1);
      break;
    case 'custom':
      // Don't auto-update dates for custom
      return null;
  }

  return {
    startDate: formatDateForInput(startDate),
    endDate: formatDateForInput(endDate),
  };
}
